<template>
  <q-layout view="lHh LpR lFf">
    <q-header
      elevated
      height-hint="98"
      :class="`bg-${appSettingsStore.currentCompany?.header_color ?? 'primary'}`">
      <UpdateManager />

      <div class="q-px-sm q-py-xs row items-center no-wrap app-header-bar">
        <q-btn
          flat
          round
          dense
          color="white"
          :icon="symRoundedMenu"
          :aria-label="$t('crudmanager.sidebar.menu')"
          :aria-expanded="drawerOpen"
          @click="toggleLeftDrawer" />

        <div class="app-header-toolbar">
          <div class="app-header-toolbar-group">
            <nav
              v-if="breadcrumbs.length > 0"
              class="app-breadcrumbs"
              :aria-label="$t('crudmanager.sidebar.breadcrumbs')">
              <template
                v-for="(crumb, idx) in breadcrumbs"
                :key="`${idx}-${crumb.label}`">
                <component
                  :is="crumb.to ? 'router-link' : 'span'"
                  :to="crumb.to"
                  :class="['app-breadcrumbs__item', { 'is-current': idx === breadcrumbs.length - 1 }]">
                  {{ crumb.label }}
                </component>
                <q-icon
                  v-if="idx < breadcrumbs.length - 1"
                  :name="symRoundedChevronRight"
                  class="app-breadcrumbs__sep" />
              </template>
            </nav>

            <div
              id="app-header-zone-tools"
              class="row items-center no-wrap app-header-zone app-header-zone--tools" />

            <div
              id="app-header-zone-search"
              class="row items-center no-wrap app-header-zone app-header-zone--search" />

            <div
              id="app-header-zone-actions"
              class="row items-center no-wrap q-gutter-xs app-header-zone app-header-zone--actions" />
          </div>
        </div>

        <div class="row items-center no-wrap app-header-actions">
          <div class="app-header-zone-divider" />
          <q-btn
            v-if="pinnedSharesStore.hidden && pinnedSharesStore.hasPinned"
            flat
            round
            dense
            color="white"
            :icon="symRoundedKeep"
            class="q-mr-sm pinned-shares-reopen"
            :aria-label="$t('crudmanager.share.pinned.reopen')"
            @click="pinnedSharesStore.show">
            <q-badge
              floating
              rounded
              color="primary"
              text-color="white"
              :label="pinnedSharesStore.count" />
            <q-tooltip>{{ $t('crudmanager.share.pinned.reopen') }}</q-tooltip>
          </q-btn>

          <NotificationCenterButton @click="showNotificationCenter = true" />
        </div>
      </div>
    </q-header>
    <q-drawer
      :model-value="drawerOpen"
      :behavior="isMobile ? 'mobile' : 'desktop'"
      :persistent="!isMobile"
      :no-swipe-close="!isMobile"
      :mini="$q.screen.width >= 768 && crudManagerStore.mainMenuIsMini"
      bordered
      side="left"
      :width="275"
      :class="[
        {
          'bg-dark': dark.isActive,
          'bg-grey-2': !dark.isActive,
        },
      ]"
      @hide="onDrawerHide">
      <div class="sidebar-shell column no-wrap full-height">
        <SidebarBrand>
          <template #actions>
            <q-btn
              flat
              round
              :icon="symRoundedRefresh"
              size="md"
              class="sidebar-brand-refresh"
              :aria-label="$t('crudmanager.common.refresh')"
              @click.stop="refreshPage">
              <q-tooltip>Rafraichir la page</q-tooltip>
            </q-btn>
          </template>
        </SidebarBrand>

        <q-list class="sidebar-pinned-top">
          <PunchHeader variant="sidebar">
            <template #left>
              <q-btn
                flat
                dense
                no-caps
                :icon="symRoundedHome"
                :label="$t('crudmanager.bottomNav.home')"
                to="/"
                exact
                class="sidebar-accueil-home" />
            </template>
          </PunchHeader>
        </q-list>

        <SidebarCategoryTabs
          :categories="sidebarCategories"
          :model-value="crudManagerStore.activeSidebarCategory"
          @update:model-value="crudManagerStore.activeSidebarCategory = $event" />

        <q-scroll-area class="col sidebar-scroll">
          <q-list class="sidebar-list">
            <CrudInstances :category="crudManagerStore.activeSidebarCategory" />
          </q-list>
        </q-scroll-area>

        <div
          id="pinbox-dock"
          class="pinbox-dock-slot" />

        <SidebarRecents />
        <SidebarFrontendSwitch v-if="REACT_FRONTEND_ENABLED" />
        <SidebarProfileCard />
      </div>
    </q-drawer>

    <q-page-container>
      <q-page class="app-container column">
        <PullToRefresh>
          <div
            id="app-filters-zone"
            class="app-filters-zone" />
          <div class="col column app-content">
            <router-view />
          </div>
        </PullToRefresh>
      </q-page>
    </q-page-container>
    <q-footer
      bordered
      :class="['app-footer', $q.dark.isActive ? 'bg-dark text-white' : 'bg-white text-dark']">
      <div
        id="app-footer-zone"
        class="app-footer-zone" />
      <BottomNav v-if="showBottomNav" />
    </q-footer>
    <TutorialOverlay />

    <!-- Notification Center -->
    <NotificationCenter
      v-model="showNotificationCenter"
      :user-id="user.user?.id"
      :auto-load="false"
      :enable-realtime="true"
      @notification-clicked="handleNotificationClicked"
      @action-executed="handleActionExecuted" />

    <!-- Pinned shares floating panel -->
    <PinnedSharesPanel />
  </q-layout>
  <LightBox />
</template>

<script setup lang="ts">
import { symRoundedChevronRight, symRoundedHome, symRoundedKeep, symRoundedMenu, symRoundedRefresh } from '@quasar/extras/material-symbols-rounded'
import { useQuasar } from 'quasar'
import BottomNav from 'src/components/bottom-nav/BottomNav.vue'
import PullToRefresh from 'src/components/pull-to-refresh/PullToRefresh.vue'
import { useBottomNavStore } from 'src/stores/useBottomNavStore'
import { customMenuItems } from 'src/CrudManager/config/customMenuItems'
import TutorialOverlay from 'src/CrudManager/Core/composites/BaseComponents/Tutorial/TutorialOverlay.vue'
import CrudInstances from 'src/CrudManager/Core/composites/Layout/CrudInstances.vue'
import LightBox from 'src/CrudManager/Core/composites/Layout/LightBox.vue'
import SidebarBrand from 'src/CrudManager/Core/composites/Layout/SidebarBrand.vue'
import SidebarCategoryTabs from 'src/CrudManager/Core/composites/Layout/SidebarCategoryTabs.vue'
import { REACT_FRONTEND_ENABLED } from 'src/config/releaseFeatures'
import SidebarFrontendSwitch from 'src/CrudManager/Core/composites/Layout/SidebarFrontendSwitch.vue'
import SidebarProfileCard from 'src/CrudManager/Core/composites/Layout/SidebarProfileCard.vue'
import SidebarRecents from 'src/CrudManager/Core/composites/Layout/SidebarRecents.vue'
import UpdateManager from 'src/CrudManager/Core/composites/Layout/UpdateManager.vue'
import NotificationCenterButton from 'src/CrudManager/Core/composites/NotificationCenter/components/NotificationCenterButton.vue'
import NotificationCenter from 'src/CrudManager/Core/composites/NotificationCenter/NotificationCenter.vue'
import { useNotificationStore } from 'src/CrudManager/Core/composites/NotificationCenter/stores/useNotificationStore'
import type { ActionExecutionResult, Notification } from 'src/CrudManager/Core/composites/NotificationCenter/types'
import PinnedSharesPanel from 'src/CrudManager/Core/composites/Layout/PinnedSharesPanel.vue'
import PunchHeader from 'src/composites/PunchComponent/PunchHeader.vue'
import { useAppSettingsStore } from 'src/CrudManager/Core/stores/useAppSettingsStore'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import { usePinnedSharesStore } from 'src/CrudManager/Core/stores/usePinnedSharesStore'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { computed, onMounted, ref, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useResponsiveDrawer } from './composables/useResponsiveDrawer'
import { useRoute, useRouter } from 'vue-router'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'

const user = useUserStore()
const router = useRouter()
const $q = useQuasar()
const { dark } = $q
const crudManagerStore = useCrudManagerStore()
const appSettingsStore = useAppSettingsStore()
const bottomNavStore = useBottomNavStore()
const notificationStore = useNotificationStore()
const pinnedSharesStore = usePinnedSharesStore()

// Notification Center state
const showNotificationCenter = ref(false)

const isMobile = computed(() => {
  return $q.screen.width < 768
})

const { drawerOpen, onDrawerHide } = useResponsiveDrawer(isMobile, toRef(crudManagerStore, 'leftDrawerOpen'))

const showBottomNav = computed(() => {
  if (!bottomNavStore.enabled) return false
  const isMobileOrTablet = $q.screen.lt.md || appSettingsStore.forceMobileMenu
  if (!isMobileOrTablet) return false
  if (isMobile.value && drawerOpen.value) return false
  return true
})

onMounted(() => {
  if (!user.user || !user.user.id) {
    router.push({ path: '/login' })
  } else {
    // Initialize notification system for logged-in user
    //initializeNotifications()
  }
})

function refreshPage() {
  localStorage.removeItem('CrudManager')
  localStorage.removeItem('punch-store')
  localStorage.removeItem('punch:routeable-order:v1')
  window.location.reload()
}

function toggleLeftDrawer() {
  drawerOpen.value = !drawerOpen.value
}

const { t } = useI18n()
const route = useRoute()
const { generateModuleUrl } = useCrudUtils()

interface Crumb {
  label: string
  to?: string
}

const breadcrumbs = computed<Crumb[]>(() => {
  const currentPath = route.path

  // Check custom menu items first
  const match = customMenuItems.find((m) => m.to && m.to === currentPath)
  if (match) {
    return [{ label: t(`crudmanager.customMenu.${match.label}`) }]
  }

  if (!crudManagerStore.isInitiated) return []

  // Match crud instance by url
  const instances = crudManagerStore.crudInstances
  const parents = instances.filter((i) => !i.parentId)
  const children = instances.filter((i) => i.parentId)

  const matchedChild = children.find((c) => generateModuleUrl(c.modelName, c.moduleName, c.props?.modelId as string) === currentPath)
  if (matchedChild) {
    const parent = parents.find((p) => p.id === matchedChild.parentId)
    const crumbs: Crumb[] = []
    if (parent) {
      const parentUrl = generateModuleUrl(parent.modelName, parent.moduleName, parent.props?.modelId as string)
      crumbs.push({
        label: t(`crudmodels.${parent.modelName}.name`, 2),
        to: parentUrl || undefined,
      })
    }
    crumbs.push({
      label: matchedChild.title || t(`crudmodels.${matchedChild.modelName}.name`, 1),
    })
    return crumbs
  }

  const matchedLister = parents.find((p) => generateModuleUrl(p.modelName, p.moduleName, p.props?.modelId as string) === currentPath)
  if (matchedLister) {
    return [{ label: t(`crudmodels.${matchedLister.modelName}.name`, 2) }]
  }

  return []
})

const sidebarCategories = computed(() => {
  const cats = new Set<string>()
  if (!crudManagerStore.isInitiated) return []
  crudManagerStore.crudInstances
    .filter((i) => !i.parentId)
    .forEach((i) => {
      const model = crudManagerStore.getModelMeta(i.modelName)
      if (model?.disabled) return
      cats.add(model?.category || 'operations')
    })
  customMenuItems.forEach((item) => cats.add(item.category))
  const order = ['operations', 'accounting', 'administration', 'ai', 'reports', 'tools', 'system']
  return Array.from(cats).sort((a, b) => {
    const ai = order.indexOf(a)
    const bi = order.indexOf(b)
    if (ai !== -1 && bi !== -1) return ai - bi
    if (ai !== -1) return -1
    if (bi !== -1) return 1
    return a.localeCompare(b)
  })
})

// Notification Center event handlers
const handleNotificationClicked = (notification: Notification) => {
  // CrudModelShared notifications get pinned to the floating panel
  // instead of navigating, so the user can keep them visible.
  const data = notification.data as Record<string, unknown> | undefined
  if (data?.type === 'crud_model_shared') {
    pinnedSharesStore.pinFromNotification({
      id: notification.id,
      created_at: notification.created_at ?? null,
      data,
    })
    notificationStore.markAsRead(notification.id)
    showNotificationCenter.value = false
    return
  }
  if (notification.model_type && notification.model_id) {
    router.push(`/editor/${notification.model_type}/${notification.model_id}`)
  }
}

const handleActionExecuted = (notification: Notification, actionName: string, result: ActionExecutionResult) => {
  console.log('Action executed:', { notification, actionName, result })
  // Handle action execution result
}
</script>

<style lang="scss" scoped>
.app-container {
  min-height: 0 !important;
  padding: 0 !important;
  // Quasar's `.column` wraps, and a wrapping flex line is as wide as its widest
  // child's content: a page with long unbreakable rows would push past a phone's edge.
  flex-wrap: nowrap;
}

.app-content {
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  padding: 0;
}

.app-footer {
  padding: 0;
}

.app-footer-zone {
  flex: 0 0 auto;
  padding: 0 16px;

  &:empty {
    display: none;
  }
}

.app-filters-zone {
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  padding: 6px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  .body--light & {
    border-bottom-color: rgba(0, 0, 0, 0.08);
  }

  &:empty {
    display: none;
  }
}

.app-header-bar {
  min-height: 48px;
  gap: 4px;
}

.app-breadcrumbs {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px 0 4px;
  min-width: 0;
  flex: 0 1 auto;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.82rem;
  overflow: hidden;
}

.app-header-toolbar {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
}

.app-header-toolbar-group {
  display: none;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;

  &:has(.app-header-zone:not(:empty)),
  &:has(.app-breadcrumbs) {
    display: flex;
  }
}

.app-header-zone {
  min-width: 0;

  &:empty {
    display: none;
  }
}

.app-header-zone--tools {
  flex: 0 0 auto;
  gap: 2px;
}

.app-header-zone--search {
  flex: 1 1 0;
  min-width: 0;
  max-width: 480px;
}

.app-header-zone--actions {
  flex: 0 0 auto;
  gap: 2px;
}

.app-breadcrumbs__item {
  color: inherit;
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
  padding: 2px 4px;
  border-radius: 4px;
  transition:
    color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(.is-current) {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
  }

  &.is-current {
    color: #ffffff;
    font-weight: 700;
  }
}

.app-breadcrumbs__sep {
  font-size: 0.95rem;
  opacity: 0.5;
  flex-shrink: 0;
}

.app-header-actions {
  gap: 2px;
}

.header-divider {
  width: 1px;
  height: 22px;
  margin: 0 8px;
  background: rgba(255, 255, 255, 0.25);
}

.sidebar-shell {
  height: 100%;
}

.sidebar-pinned-top {
  padding: 2px 0 4px;
}

.sidebar-accueil-home {
  flex-shrink: 0;
  align-self: stretch;
  padding: 0 10px;
  border-radius: 0;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  background: rgba(255, 255, 255, 0.04);
  transition: background 0.15s ease;
  min-height: 0;

  :deep(.q-btn__content) {
    flex-direction: column;
    gap: 1px;
    line-height: 1.1;
    height: 100%;
    justify-content: center;
  }

  :deep(.q-icon) {
    font-size: 1.15rem;
    margin: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  &.q-router-link--active {
    color: var(--q-primary);
  }
}

.body--light .sidebar-accueil-home {
  background: rgba(0, 0, 0, 0.03);

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
}

:deep(.sidebar-accueil-punch) {
  margin: 4px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  overflow: hidden;

  .sidebar-accueil-punch__row > .sidebar-accueil-home + .col {
    border-left: 1px solid rgba(255, 255, 255, 0.08);
  }

  .punch-card {
    width: 100%;
    max-width: none;
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;

    &:hover {
      background: rgba(255, 255, 255, 0.04) !important;
    }
  }
}

.body--light :deep(.sidebar-accueil-punch) {
  background: rgba(0, 0, 0, 0.04);

  .sidebar-accueil-punch__row > .sidebar-accueil-home + .col {
    border-left-color: rgba(0, 0, 0, 0.08);
  }

  .punch-card:hover {
    background: rgba(0, 0, 0, 0.04) !important;
  }
}

.sidebar-brand-refresh {
  opacity: 0.85;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }

  :deep(.q-icon) {
    font-size: 1.4rem;
  }
}

.sidebar-top-item--pill {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  min-height: 40px;

  :deep(.q-item__label),
  :deep(.q-item__section--main) {
    font-size: 0.844rem;
    font-weight: 500;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &.q-router-link--active {
    background: color-mix(in srgb, var(--q-primary) 10%, transparent);
    border-color: color-mix(in srgb, var(--q-primary) 20%, transparent);
  }
}

.body--light .sidebar-top-item--pill {
  background: rgba(0, 0, 0, 0.015);
  border-color: rgba(0, 0, 0, 0.04);

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
}

.sidebar-scroll {
  min-height: 0;
}

// Dock slot is purely a Teleport target. Panel becomes position:absolute
// when docked, so the slot itself never takes layout space — kept as an
// empty `display: contents` element inside the drawer's flex column.
.pinbox-dock-slot {
  display: contents;
}

.sidebar-list {
  padding: 4px 0 12px;
}

.sidebar-top-item {
  border-radius: 8px;
  margin: 1px 8px;
  min-height: 36px;
  padding: 4px 8px;
  transition:
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  :deep(.q-icon) {
    font-size: 1.15rem;
    opacity: 0.7;
    transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  }

  :deep(.q-item__section--side) {
    padding-right: 16px;
    min-width: 0;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);

    :deep(.q-icon) {
      opacity: 1;
    }
  }

  &.q-router-link--active {
    background-color: color-mix(in srgb, var(--q-primary) 10%, transparent);
    color: var(--q-primary);

    :deep(.q-icon) {
      opacity: 1;
    }
  }
}

.body--light {
  .sidebar-top-item:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.pinned-shares-reopen {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.65);
}
</style>

<style lang="scss">
.app-header-create-btn {
  margin-left: 4px;
  border-radius: 8px;

  &.q-btn--outline::before {
    border-width: 2px;
  }

  .q-icon {
    font-size: 1.25rem;
    font-weight: 700;
  }
}

.app-header-zone-divider {
  width: 1px;
  height: 18px;
  margin: 0 6px;
  background: rgba(255, 255, 255, 0.18);
  flex-shrink: 0;
}
</style>
